import { useCallback, useEffect, useState } from 'react';

import type { VideoPlayer } from 'bundles/item-lecture';
import { useDubbingSubtitle } from 'bundles/video-player/components/hooks/useDubbingSubtitle';
import { VIDEO_PLAYER_SUBTITLE_NOT_SET } from 'bundles/video-player/constants';
import type { Track, Tracks } from 'bundles/video-player/types/Track';
import type { VideoPlayerEventingV3UIFunction } from 'bundles/video-player/types/VideoPlayer';
import userPreferences from 'bundles/video-player/utils/userPreferences';

type UseSubtitleMenuControlsArgs = {
  defaultSubtitleLanguage: string;
  player: VideoPlayer;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
  selectedAudioLanguage?: { id: string; languageKey: string; label: string };
  defaultLanguageKey?: string;
  shouldShowNudgeOnSubtitleChange?: (languageCode: string) => void;
};

export const useSubtitleMenuControls = ({
  player,
  defaultSubtitleLanguage,
  trackVideoPlayerClickEvent,
  selectedAudioLanguage,
  defaultLanguageKey,
  shouldShowNudgeOnSubtitleChange,
}: UseSubtitleMenuControlsArgs) => {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const isDubbedAudioEnabled = selectedAudioLanguage?.languageKey !== defaultLanguageKey;
  const [tracks, setTracks] = useState<Tracks>([]);

  const setTrackAsShowing = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (track: Track) => {
      const { tracks_ } = player.textTracks();
      for (let i = 0; i < tracks_.length; i += 1) {
        const t = tracks_[i];

        if (t.kind === 'captions' && t.label === track.label && !t.id.includes('-dub')) {
          t.mode = 'showing';
        } else {
          t.mode = 'disabled';
        }
      }
    },
    [player]
  );

  const setDubbedTrackAsShowing = useCallback(
    (track: Track) => {
      const { tracks_ } = player.textTracks();
      for (let i = 0; i < tracks_.length; i += 1) {
        const t = tracks_[i];

        if (t.kind === 'captions' && t.label === track.label && t.id.includes('-dub')) {
          t.mode = 'showing';
        } else {
          t.mode = 'disabled';
        }
      }
    },
    [player]
  );

  const getTracks = useCallback<() => $TSFixMe>(() => {
    const { tracks_ = [] } = player?.textTracks() || {};
    const dubbedTracks = tracks_.filter((track: $TSFixMe) => track.id.includes('-dub'));
    const dubbedSubtitleTrack = dubbedTracks.filter(
      (track: $TSFixMe) => track.language === selectedAudioLanguage?.languageKey
    );
    const nonDubbedTracks = tracks_
      .filter((track: $TSFixMe) => track.kind === 'captions' && !track.id.includes('-dub'))
      .sort((a: $TSFixMe, b: $TSFixMe) => {
        const labelA = a.label.toUpperCase();
        const labelB = b.label.toUpperCase();
        const order = labelA > labelB ? 1 : 0;
        return labelA < labelB ? -1 : order;
      });
    return selectedAudioLanguage?.languageKey !== defaultLanguageKey ? dubbedSubtitleTrack : nonDubbedTracks;
  }, [player, selectedAudioLanguage?.languageKey, defaultLanguageKey]);

  const onSubtitleLanguageChoiceClick = (
    track: Track,
    disableTrackingForModernVideoPlayerExperiment?: boolean,
    isDubbedSubtitle = false
  ) => {
    if (currentTrack) {
      currentTrack.mode = 'disabled';
    }
    if (!isDubbedAudioEnabled) {
      userPreferences.set('subtitleLanguage', track ? track.language : VIDEO_PLAYER_SUBTITLE_NOT_SET);
    } else {
      userPreferences.set('dubbedSubtitleLanguage', track ? track.language : VIDEO_PLAYER_SUBTITLE_NOT_SET);
    }
    if (!disableTrackingForModernVideoPlayerExperiment) {
      trackVideoPlayerClickEvent('video_subtitle_option', {
        previousSubtitleLanguage: userPreferences.get('subtitleLanguage'),
        subtitleLanguage: track.language,
      });
    }
    if (isDubbedSubtitle) {
      setDubbedTrackAsShowing(track);
    } else {
      setTrackAsShowing(track);
    }
    setCurrentTrack(track);
    shouldShowNudgeOnSubtitleChange?.(track.language);

    player.trigger('trackchange');
    player.trigger('subtitleschange', { previousLanguage: currentTrack?.language ?? '' });
  };

  useEffect(() => {
    const getCurrentTrack = () => {
      return player.textTracks().tracks_.find((t: $TSFixMe) => t.mode === 'showing') || null;
    };

    const getDefaultTrack = () => {
      return player.textTracks().tracks_.find((track: $TSFixMe) => track.language === defaultSubtitleLanguage) || null;
    };

    const onTrackChange = () => {
      setCurrentTrack(getCurrentTrack());
    };

    const onTrackLoad = () => {
      const defaultTrack = getDefaultTrack();
      const currTrack = getCurrentTrack();
      if (!currTrack && defaultTrack) {
        setTrackAsShowing(defaultTrack);
      }
      // check nudge visibility on track load
      shouldShowNudgeOnSubtitleChange?.(currTrack?.language ?? '');
      setCurrentTrack(currTrack);
      setTracks(getTracks());
    };

    player.on('trackchange', onTrackChange);
    player.on('trackloaded', onTrackLoad);

    return () => {
      player.off('trackchange', onTrackChange);
      player.off('trackloaded', onTrackLoad);
    };
  }, [currentTrack, defaultSubtitleLanguage, getTracks, player, setTrackAsShowing, shouldShowNudgeOnSubtitleChange]);

  const onSubtitleOffClick = (isDubbedAudioSelected?: boolean) => {
    if (currentTrack) {
      currentTrack.mode = 'disabled';
    }
    if (isDubbedAudioSelected) {
      userPreferences.set('dubbedSubtitleLanguage', VIDEO_PLAYER_SUBTITLE_NOT_SET);
    } else {
      userPreferences.set('subtitleLanguage', VIDEO_PLAYER_SUBTITLE_NOT_SET);
    }

    setCurrentTrack(null);

    player.trigger('trackchange');
    player.trigger('subtitleschange', { previousLanguage: currentTrack?.language ?? '' });
  };

  useDubbingSubtitle({
    getTracks,
    defaultLanguageKey,
    onSubtitleOffClick,
    isDubbedAudioEnabled,
    onSubtitleLanguageChoiceClick,
    player,
  });

  return {
    onSubtitleLanguageChoiceClick,
    onSubtitleOffClick,
    currentTrack,
    tracks,
  };
};
